import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalSize, ModalVariant } from '@/features/configuration-management/location/table/Column';
import { EditIcon, LockCloseIcon, LockOpenIcon, TrashIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { StandardizedApiError } from '@/services/client/types';
import {
  DeletePromotionalBannerPayload,
  PromotionalBanner,
  PromotionalBannerStatus,
  UpdatePromotionalBannerStatusPayload,
} from '@/services/promotionalBanner/promotionalBanner.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { BannerManagementFormContainer } from '../BannerManagementFormContainer';

export function getPromotionBannerColumns(
  t: (key: string, values?: Record<string, unknown>) => string,
  locale: 'en' | 'ja',
  openConfirmation: (data: {
    title: string;
    description?: string | undefined;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    isSubmitting?: 'submit' | 'cancel' | null;
  }) => void,
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle: string | null;
    okText: React.ReactNode;
    component: React.ReactNode;
    cancelText?: string | React.ReactNode;
    size?: ModalSize;
    onCancel?: () => void;
    image?: boolean;
    isSubmitting?: 'submit' | 'cancel' | null;
  }) => void,
  canEditBanner: boolean,
  mutateAsync: UseMutateAsyncFunction<
    {
      message: string;
      data: PromotionalBanner;
    },
    StandardizedApiError,
    UpdatePromotionalBannerStatusPayload,
    unknown
  >,
  deleteMutation: UseMutateAsyncFunction<
    {
      message: string;
      data: PromotionalBanner;
    },
    StandardizedApiError,
    DeletePromotionalBannerPayload,
    unknown
  >,
): ColumnDef<PromotionalBanner>[] {
  return [
    {
      accessorKey: 'sno',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => <div className="font-medium text-xs">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'title',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.title')} />,
      cell: ({ row }) => <div className="text-xs font-medium">{row.getValue('title')}</div>,
      meta: { label: 'Title' },
      enableHiding: false,
    },
    {
      accessorKey: 'locationName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.locationTitle')} />
      ),
      cell: ({ row }) => {
        const locationTitle =
          locale === 'en'
            ? row.original.locationNameEng || row.original.locationName || '-'
            : row.original.locationName || row.original.locationNameEng || '-';
        return <div className="text-xs font-medium">{locationTitle}</div>;
      },
      meta: { label: 'Location Title' },
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.imageDescription')} />
      ),
      cell: ({ row }) => (
        <div className="truncate max-w-[30rem] text-xs font-medium">{row.getValue('description')}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'image',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.image')} />,
      cell: ({ row }) => (
        <div className="w-[72px] h-[32px]">
          <img src={row.original.image.path} alt={row.original.image.alt} className="h-full w-full object-cover" />
        </div>
      ),
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => (
        <div
          className={`text-caption font-medium  ${row.getValue('status') === PromotionalBannerStatus.ACTIVE ? 'text-success' : 'text-destructive-text'}`}
        >
          {row.getValue('status') === PromotionalBannerStatus.ACTIVE
            ? t('common.page.table.active')
            : row.getValue('status') === PromotionalBannerStatus.BLOCKED
              ? t('common.page.table.blocked')
              : null}
        </div>
      ),
      enableHiding: false,
    },
    {
      id: 'createdDate',
      enableColumnFilter: true,
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const raw = Number(row.getValue('createdDate'));
        return <div className="text-xs font-medium">{formatDate(raw)}</div>;
      },
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          <Can rule={{ allOf: [PERMS.promotionalBanner.details] }}>
            {row.original.status === PromotionalBannerStatus.ACTIVE && (
              <Button
                variant={'ghost'}
                className="p-0 m-0"
                onClick={() =>
                  onOpen({
                    modalTitle: 'promoManagement.promotionalBanners.modal.title',
                    title: 'Manage Promotional banners',
                    okText: canEditBanner ? t('common.page.button.submit') : '',
                    size: 'lg',
                    component: <BannerManagementFormContainer promotionId={row.original.sno} />,
                  })
                }
              >
                <EditIcon className="text-foreground-subtle size-4" />
              </Button>
            )}
          </Can>
          <Can rule={{ allOf: [PERMS.promotionalBanner.status] }}>
            <Button
              variant={'ghost'}
              className="p-0 m-0"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description:
                    row.getValue('status') === PromotionalBannerStatus.ACTIVE
                      ? t('common.page.modal.blockMessage')
                      : t('common.page.modal.unblockMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.confirm'),
                  onAction: async () => {
                    const payload = {
                      promotionalId: row.original.sno,
                      status:
                        row.original.status === PromotionalBannerStatus.ACTIVE
                          ? PromotionalBannerStatus.BLOCKED
                          : PromotionalBannerStatus.ACTIVE,
                    };
                    await mutateAsync(payload);
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === PromotionalBannerStatus.ACTIVE ? (
                <LockCloseIcon className="text-destructive-text size-4" />
              ) : (
                <LockOpenIcon className="text-link size-4" />
              )}
            </Button>
          </Can>
          <Can rule={{ allOf: [PERMS.promotionalBanner.delete] }}>
            {row.original.status === PromotionalBannerStatus.ACTIVE && (
              <Button
                variant={'ghost'}
                className="p-0 m-0"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description: t('common.page.modal.deleteItemMessage'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.confirm'),
                    onAction: async () => {
                      const payload = {
                        promotionalId: row.original.sno,
                        status: PromotionalBannerStatus.DELETED,
                      };
                      await deleteMutation(payload);
                    },
                    onCancel: () => {},
                    size: 'lg',
                    variant: 'destructive',
                  });
                }}
              >
                <TrashIcon className="text-destructive-text" />
              </Button>
            )}
          </Can>
        </div>
      ),
      size: 40,
      enableHiding: false,
      enableSorting: false,
    },
  ];
}
